/**
 * WafTab.tsx
 *
 * Outer WAF tab shell — owns form state, persistence, Under Attack Mode, and
 * the top-level tab layout. Inner tabs are imported from ./waf/:
 *   - ProtectionTab  — DLP / GraphQL / DDoS / threat-scoring settings
 *   - AiRulesetTab   — ML-scored rule catalog with per-rule / per-category toggles
 *   - WafCustomRules — wirefilter custom-rule CRUD (includes its own dialog)
 */
import { useEffect, useMemo, useState } from 'react';
import { Flame, BrainCircuit } from 'lucide-react';
import { Section, SettingRow, ActionBar } from '@vnetwork/ui/layout';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
  Card, CardHeader, CardTitle, CardDescription, CardBody,
  Button, Switch, Skeleton,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@vnetwork/ui/primitives';
import { StatusPill, FlowDiagram, FlowRow, type FlowNode } from '@vnetwork/ui/data';
import { Alert, Banner } from '@vnetwork/ui/feedback';
import { useToast } from '@vnetwork/ui/feedback';
import { useCdnConfig, useUpdateCdnConfig, useDeleteCdnConfig } from '@/api/manticore';
import { errorMessage } from '@/api/error-message';
import { trackFeatureAction } from '@/lib/analytics-events';
import { configTone, configLabel, toneColor } from '../../shared';
import { jsonEqual } from '../../_shared/ConfigFormShell';
import { CdnAspectDrift } from '../../_shared/VendorDrift';
import { EdgePipelineExplainer } from '../../_shared/EdgePipeline';
import { ProtectionTab } from './waf/ProtectionTab';
import { AiRulesetTab } from './waf/AiRulesetTab';
import { WafCustomRules } from './waf/WafCustomRules';
import { useWafRules } from '@/api/manticore';
import { hydrate, WAF_FORM_DEFAULT, type WafForm, type AiCfg } from './waf/waf-types';

const UA_KEY = (d: string) => `under_attack_waf_${d}`;

export function WafTab({ domain, domainId }: { domain: string; domainId: string }) {
  const { show } = useToast();
  const { data: cfg, isLoading } = useCdnConfig(domainId, 'waf', { vendorMarkers: ['mode', 'ai', 'enabled'] });
  const isConfigured = !!cfg;

  const [form, setForm] = useState<WafForm>(WAF_FORM_DEFAULT);
  const [snap, setSnap] = useState('');
  const [tab, setTab] = useState('overview');

  useEffect(() => {
    const next = cfg ? hydrate(cfg) : WAF_FORM_DEFAULT;
    setForm(next);
    setSnap(JSON.stringify(next));
  }, [cfg]);

  const dirty = useMemo(() => !jsonEqual(form, JSON.parse(snap || '{}')), [form, snap]);
  const set = <K extends keyof WafForm>(k: K, v: WafForm[K]) => setForm((f) => ({ ...f, [k]: v }));
  const setAi = <K extends keyof AiCfg>(k: K, v: AiCfg[K]) => setForm((f) => ({ ...f, ai: { ...f.ai, [k]: v } }));

  const update = useUpdateCdnConfig(domainId, 'waf', {
    onSuccess: () => {
      trackFeatureAction('cdn', 'waf', isConfigured ? 'edit' : 'create');
      show({ tone: 'success', title: 'WAF saved' });
    },
    onError: (e) => show({ tone: 'danger', title: 'Could not save WAF', description: errorMessage(e, 'Could not save WAF') }),
  });
  const remove = useDeleteCdnConfig(domainId, 'waf', {
    onSuccess: () => {
      trackFeatureAction('cdn', 'waf', 'delete');
      show({ tone: 'success', title: 'WAF removed' });
    },
    onError: (e) => show({ tone: 'danger', title: 'Could not remove WAF', description: errorMessage(e, 'Could not remove WAF') }),
  });
  const busy = update.isPending || remove.isPending;
  const saveForm = (next: WafForm) => update.mutate(next as unknown as Record<string, unknown>);

  // Under Attack Mode — snapshot current config, force block + tighten, restore.
  const underAttackActive = typeof window !== 'undefined' && !!window.localStorage.getItem(UA_KEY(domainId));
  const enableUnderAttack = () => {
    try { window.localStorage.setItem(UA_KEY(domainId), snap || JSON.stringify(form)); } catch { /* ignore */ }
    const next: WafForm = { ...form, enabled: true, mode: 'block', managed_rules_enabled: true };
    setForm(next); saveForm(next);
    show({ tone: 'warning', title: 'Under Attack Mode enabled' });
  };
  const restoreUnderAttack = () => {
    try {
      const saved = window.localStorage.getItem(UA_KEY(domainId));
      if (saved) { const prev = JSON.parse(saved) as WafForm; setForm(prev); saveForm(prev); }
      window.localStorage.removeItem(UA_KEY(domainId));
    } catch { /* ignore */ }
    show({ tone: 'success', title: 'Configuration restored' });
  };

  if (isLoading) return <Section title="WAF"><Skeleton style={{ height: 360 }} /></Section>;

  const protectionOn = !!(form.ai.dlp_mode || form.ai.ddos_sensitivity);
  const flowColumns: FlowNode[][] = [
    [{ id: 'in', title: 'Incoming', to: ['managed'] }],
    [{ id: 'managed', title: 'Managed rules', dotColor: form.managed_rules_enabled ? toneColor('success') : undefined, to: ['custom'], children: <FlowRow label="Status" value={form.managed_rules_enabled ? 'Enabled' : 'Off'} /> }],
    [{ id: 'custom', title: 'Custom rules', to: ['ai'], children: <FlowRow label="Rules" value={<WafRuleCount domainId={domainId} />} /> }],
    [{ id: 'ai', title: 'AI ruleset', dotColor: form.ai.enabled ? toneColor('info') : undefined, to: ['prot'], children: <FlowRow label="Mode" value={form.ai.enabled ? form.ai.ruleset_mode : 'Off'} /> }],
    [{ id: 'prot', title: 'Protection', to: ['ml'], children: <FlowRow label="Status" value={protectionOn ? 'Active' : 'Off'} /> }],
    [{ id: 'ml', title: 'AI WAF ML', dotColor: toneColor('warning'), to: ['origin'], children: <FlowRow label="State" value="Maintenance" /> }],
    [{ id: 'origin', title: 'Origin' }],
  ];

  return (
    <Section
      title="WAF (Web Application Firewall)"
      description={`Protect ${domain} from common web attacks — SQL injection, XSS, and path traversal.`}
      actions={
        <div style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center' }}>
          {underAttackActive ? <StatusPill tone="danger">Under Attack</StatusPill> : null}
          <StatusPill tone={configTone(form.enabled)}>{form.enabled ? `Enabled · ${form.mode}` : configLabel(isConfigured)}</StatusPill>
        </div>
      }
    >
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <CdnAspectDrift domainId={domainId} path="waf" />
        <EdgePipelineExplainer />

        <Banner
          tone="danger"
          icon={<Flame size={18} />}
          title="Emergency response"
          description="One click force-enables WAF block mode and tightens bot-management thresholds during an active attack."
          action={
            underAttackActive ? (
              <Button variant="secondary" size="sm" disabled={busy} onClick={restoreUnderAttack}>Restore configuration</Button>
            ) : (
              <Button variant="danger" size="sm" startIcon={<Flame size={15} />} disabled={busy} onClick={enableUnderAttack}>
                Enable Under Attack Mode
              </Button>
            )
          }
        />

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="managed">Managed Rules</TabsTrigger>
            <TabsTrigger value="protection">Protection</TabsTrigger>
            <TabsTrigger value="rules">Custom Rules</TabsTrigger>
            <TabsTrigger value="ai">AI Ruleset</TabsTrigger>
            <TabsTrigger value="ml">AI WAF ML</TabsTrigger>
          </TabsList>

          {/* Overview */}
          <TabsContent value="overview">
            <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
              <Card>
                <CardBody style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
                  <SettingRow title="WAF enabled" description="Master switch for the firewall." control={<Switch checked={form.enabled} onCheckedChange={(v) => set('enabled', v)} />} />
                  <SettingRow
                    title="Mode when a rule matches"
                    description="Block rejects matched requests; Detect only logs them. Default action when nothing matches is Allow."
                    control={
                      <Select value={form.mode} onValueChange={(v) => set('mode', v)}>
                        <SelectTrigger style={{ width: 200 }}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="block">Block matched requests</SelectItem>
                          <SelectItem value="detect">Detect only (log)</SelectItem>
                        </SelectContent>
                      </Select>
                    }
                  />
                </CardBody>
              </Card>
              {form.mode === 'detect' && form.enabled && <Alert tone="warning">WAF is in detect mode — matches are logged but not blocked.</Alert>}
              <Card>
                <CardHeader><CardTitle>Request pipeline</CardTitle><CardDescription>How a request flows through the WAF tiers. Click a stage to inspect it.</CardDescription></CardHeader>
                <CardBody style={{ overflowX: 'auto' }}>
                  <FlowDiagram columns={flowColumns} />
                </CardBody>
              </Card>
              <ConfigActions isConfigured={isConfigured} dirty={dirty} busy={busy} saving={update.isPending} onSave={() => saveForm(form)} onRemove={() => remove.mutate()} onCancel={() => snap && setForm(JSON.parse(snap))} />
            </div>
          </TabsContent>

          {/* Managed Rules */}
          <TabsContent value="managed">
            <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
              <Card>
                <CardHeader>
                  <CardTitle>Managed rules</CardTitle>
                  <CardDescription>Built-in rules covering common attack patterns (SQLi, XSS, path traversal). Maintained and updated automatically; cover OWASP Top 10 with low false-positive tuning.</CardDescription>
                </CardHeader>
                <CardBody>
                  <SettingRow title="Managed rules enabled" description="Disable only if you rely exclusively on custom rules or the AI engine." control={<Switch checked={form.managed_rules_enabled} onCheckedChange={(v) => set('managed_rules_enabled', v)} />} />
                </CardBody>
              </Card>
              <ConfigActions isConfigured={isConfigured} dirty={dirty} busy={busy} saving={update.isPending} onSave={() => saveForm(form)} onRemove={() => remove.mutate()} onCancel={() => snap && setForm(JSON.parse(snap))} />
            </div>
          </TabsContent>

          {/* Protection */}
          <TabsContent value="protection">
            <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
              <ProtectionTab ai={form.ai} setAi={setAi} />
              <ConfigActions isConfigured={isConfigured} dirty={dirty} busy={busy} saving={update.isPending} onSave={() => saveForm(form)} onRemove={() => remove.mutate()} onCancel={() => snap && setForm(JSON.parse(snap))} />
            </div>
          </TabsContent>

          {/* Custom Rules */}
          <TabsContent value="rules"><WafCustomRules domainId={domainId} /></TabsContent>

          {/* AI Ruleset */}
          <TabsContent value="ai">
            <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
              <AiRulesetTab domainId={domainId} ai={form.ai} setAi={setAi} />
              <ConfigActions isConfigured={isConfigured} dirty={dirty} busy={busy} saving={update.isPending} onSave={() => saveForm(form)} onRemove={() => remove.mutate()} onCancel={() => snap && setForm(JSON.parse(snap))} />
            </div>
          </TabsContent>

          {/* AI WAF ML */}
          <TabsContent value="ml">
            <Card>
              <CardBody style={{ textAlign: 'center', padding: 'var(--vn-space-8)' }}>
                <BrainCircuit size={40} style={{ color: 'var(--vn-fg-subtle)' }} />
                <div style={{ fontWeight: 'var(--vn-font-weight-semibold)', marginTop: 'var(--vn-space-3)' }}>ML inference engine — under maintenance</div>
                <p style={{ fontSize: 'var(--vn-font-size-md)', color: 'var(--vn-fg-muted)', maxWidth: 560, margin: 'var(--vn-space-2) auto 0' }}>
                  The LightGBM ONNX machine-learning inference engine is under maintenance to improve async runtime compatibility. Attack detection remains fully operational via the AI Ruleset (~2,280 pattern rules) and Managed Rules.
                </p>
                <div style={{ display: 'flex', gap: 'var(--vn-space-6)', justifyContent: 'center', marginTop: 'var(--vn-space-4)', fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
                  <span>AI Ruleset: <strong style={{ color: 'var(--vn-success-text)' }}>Active</strong></span>
                  <span>Managed Rules: <strong style={{ color: 'var(--vn-success-text)' }}>Active</strong></span>
                  <span>ML Engine: <strong style={{ color: 'var(--vn-warning-text)' }}>Maintenance</strong></span>
                </div>
              </CardBody>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Section>
  );
}

// ── Shared save/cancel/remove action bar ────────────────────────────────────
function ConfigActions({ isConfigured, dirty, busy, saving, onSave, onRemove, onCancel }: {
  isConfigured: boolean;
  dirty: boolean;
  busy: boolean;
  saving: boolean;
  onSave: () => void;
  onRemove: () => void;
  onCancel: () => void;
}) {
  return (
    <ActionBar
      start={isConfigured ? <Button variant="secondary" disabled={busy} onClick={onRemove}>Remove</Button> : undefined}
      end={
        <>
          {isConfigured && <Button variant="secondary" disabled={!dirty || busy} onClick={onCancel}>Cancel</Button>}
          <Button disabled={(isConfigured && !dirty) || busy} onClick={onSave}>{saving ? 'Saving…' : isConfigured ? 'Save' : 'Create'}</Button>
        </>
      }
    />
  );
}

// Small inline helper — renders WAF custom rule count for the pipeline diagram.
function WafRuleCount({ domainId }: { domainId: string }) {
  const { data: rules = [] } = useWafRules(domainId);
  return <>{rules.length}</>;
}
